import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deletePalmares,
  getPalmaresByAdherentId,
  updatePalmares,
} from "../../../../services/palmaresServices";
import { getAdherentsByUser } from "../../../../services/adherentServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_PALMARES_FILTERS,
  countActivePalmaresFilters,
  dedupePalmaresItems,
  filterPalmaresItems,
  getPalmaresKey,
  getPalmaresStats,
  getPalmaresYears,
  normalizeAdherentsResponse,
  normalizePalmaresResponse,
  sortPalmaresItems,
} from "../utils/palmaresUtils";

export function usePalmaresPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);

  const [palmares, setPalmares] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_PALMARES_FILTERS);

  const [selectedPalmares, setSelectedPalmares] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [adherentsLoading, setAdherentsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);

  const selectedAdherent = useMemo(() => {
    if (!adherents.length) return null;

    return (
      adherents.find(
        (adherent) => Number(adherent.id) === Number(selectedAdherentId),
      ) || adherents[0]
    );
  }, [adherents, selectedAdherentId]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchAdherents = useCallback(async () => {
    setAdherentsLoading(true);

    try {
      const response = await getAdherentsByUser();
      const loadedAdherents = normalizeAdherentsResponse(response);

      setAdherents(loadedAdherents);

      if (loadedAdherents.length > 0) {
        setSelectedAdherentId((currentId) => {
          const stillExists = loadedAdherents.some(
            (adherent) => Number(adherent.id) === Number(currentId),
          );

          return stillExists ? currentId : loadedAdherents[0].id;
        });
      } else {
        setSelectedAdherentId(null);
        setPalmares([]);

        notificationRef.current?.(
          "Aucun adhérent n'est rattaché à votre compte.",
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des profils judokas.",
        "error",
      );
    } finally {
      setAdherentsLoading(false);
    }
  }, []);

  const fetchPalmares = useCallback(
    async ({ silent = false, adherentId = selectedAdherentId } = {}) => {
      if (!adherentId) {
        setPalmares([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const response = await getPalmaresByAdherentId(adherentId);
        const safePalmares = normalizePalmaresResponse(response);
        const uniquePalmares = dedupePalmaresItems(safePalmares);

        setPalmares(sortPalmaresItems(uniquePalmares));

        if (!silent && !isInitialLoad) {
          notificationRef.current?.("Palmarès actualisé.", "success");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération du palmarès :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération du palmarès.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedAdherentId],
  );

  useEffect(() => {
    fetchAdherents();
  }, [fetchAdherents]);

  useEffect(() => {
    if (selectedAdherentId) {
      fetchPalmares({ silent: true, adherentId: selectedAdherentId });
    } else if (!adherentsLoading) {
      setLoading(false);
    }
  }, [adherentsLoading, fetchPalmares, selectedAdherentId]);

  const selectAdherent = useCallback((adherentId) => {
    setSelectedAdherentId(adherentId);
    setPalmares([]);
    setSelectedPalmares(null);
    setSelectedImage(null);
    setIsEditModalOpen(false);
    hasLoadedOnceRef.current = false;
  }, []);

  const filteredPalmares = useMemo(() => {
    return filterPalmaresItems(palmares, filters);
  }, [palmares, filters]);

  const stats = useMemo(() => {
    return getPalmaresStats(palmares);
  }, [palmares]);

  const years = useMemo(() => {
    return getPalmaresYears(palmares);
  }, [palmares]);

  const activeFiltersCount = useMemo(() => {
    return countActivePalmaresFilters(filters);
  }, [filters]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_PALMARES_FILTERS);
  }, []);

  const handleEdit = useCallback((palmaresItem) => {
    setSelectedPalmares(palmaresItem);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setSelectedPalmares(null);
    setIsEditModalOpen(false);
  }, []);

  const handleSave = useCallback(
    async (updatedPalmares) => {
      if (!updatedPalmares?.id) {
        notificationRef.current?.(
          "Impossible d'enregistrer : palmarès invalide.",
          "error",
        );
        return;
      }

      setSaving(true);

      try {
        await updatePalmares(updatedPalmares.id, updatedPalmares);

        setPalmares((prev) =>
          sortPalmaresItems(
            prev.map((item) =>
              Number(item.id) === Number(updatedPalmares.id)
                ? updatedPalmares
                : item,
            ),
          ),
        );

        notificationRef.current?.("Palmarès modifié avec succès.", "success");
        handleCloseEditModal();
      } catch (error) {
        console.error("Erreur lors de la modification du palmarès :", error);

        notificationRef.current?.(
          "Erreur lors de la modification du palmarès.",
          "error",
        );
      } finally {
        setSaving(false);
      }
    },
    [handleCloseEditModal],
  );

  const handleDelete = useCallback(
    async (palmaresItem) => {
      const itemKey = getPalmaresKey(palmaresItem);

      if (deletingIds.includes(itemKey)) return;

      if (!palmaresItem?.id) {
        notificationRef.current?.(
          "Impossible de supprimer ce palmarès : identifiant manquant.",
          "error",
        );
        return;
      }

      setDeletingIds((prev) => [...prev, itemKey]);

      try {
        await deletePalmares(palmaresItem.id);

        setPalmares((prev) =>
          prev.filter((item) => Number(item.id) !== Number(palmaresItem.id)),
        );

        notificationRef.current?.("Palmarès supprimé avec succès.", "success");
      } catch (error) {
        console.error("Erreur lors de la suppression du palmarès :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression du palmarès.",
          "error",
        );
      } finally {
        setDeletingIds((prev) => prev.filter((id) => id !== itemKey));
      }
    },
    [deletingIds],
  );

  const handleDeleteWithConfirm = useCallback(
    async (palmaresItem) => {
      const confirmDelete = window.confirm(
        "Supprimer ce résultat de votre palmarès ?\n\nCette action ne supprimera pas l'événement d'origine, mais retirera cette ligne de votre palmarès.",
      );

      if (!confirmDelete) return;

      await handleDelete(palmaresItem);
    },
    [handleDelete],
  );

  const handleImageClick = useCallback((url) => {
    if (!url) return;
    setSelectedImage(url);
  }, []);

  const handleOpenLink = useCallback((url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  return {
    adherents,
    selectedAdherent,
    selectedAdherentId,
    adherentsLoading,

    palmares,
    filteredPalmares,
    filters,
    selectedPalmares,
    selectedImage,
    isEditModalOpen,

    loading,
    refreshing,
    saving,
    deletingIds,

    activeFiltersCount,
    stats,
    years,

    fetchAdherents,
    fetchPalmares,
    selectAdherent,

    setSelectedImage,
    handleFilterChange,
    resetFilters,
    handleEdit,
    handleCloseEditModal,
    handleSave,
    handleDeleteWithConfirm,
    handleImageClick,
    handleOpenLink,
  };
}